import { useEffect, useState } from 'react';
import { Plus, Trash2, RefreshCw, X, Key, Copy, Check, DollarSign, Network, Eye, Edit, ShoppingCart, Search, ShieldAlert, Globe, CalendarClock } from 'lucide-react';
// Network is used in the Access column badges in the table
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../components/ui/Toast';
import { ApiKeyInfo, ApiResponse } from '../types';
import { format } from 'date-fns';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonRows } from '../components/ui/Skeleton';
import { useConfirm } from '../components/ui/ConfirmDialog';

export default function ApiKeysPage() {
  const { fetchApi, isSuperAdmin } = useApi();
  const toast = useToast();
  const [keys, setKeys] = useState<ApiKeyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKeyInfo | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    loadKeys();
  }, []);

  async function loadKeys() {
    try {
      const res = await fetchApi<ApiResponse<ApiKeyInfo[]>>('/api-keys');
      if (res.data) setKeys(res.data);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = async (key: ApiKeyInfo) => {
    if (!(await confirm({
      title: 'Delete API key?',
      message: `Delete API key "${key.name}"? Any applications using this key will lose access.`,
      tone: 'danger',
    }))) return;

    try {
      await fetchApi(`/api-keys/${key.id}`, { method: 'DELETE' });
      loadKeys();
    } catch (error) {
      toast.error('Failed to delete API key', error instanceof Error ? error.message : undefined);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="API Keys"
        description={isSuperAdmin
          ? 'Overview of all API keys across all users'
          : 'Manage API keys for programmatic access'}
        actions={
          <button
            onClick={() => { setNewKey(null); setShowModal(true); }}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create API Key
          </button>
        }
      />

      {newKey && (
        <NewKeyBanner apiKey={newKey} onDismiss={() => setNewKey(null)} />
      )}

      {loading ? (
        <div className="card overflow-hidden">
          <table className="w-full"><tbody><SkeletonRows rows={5} cols={8} /></tbody></table>
        </div>
      ) : keys.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={Key}
            title="No API keys yet"
            description="API keys allow external applications to access the Domain Management API."
            action={
              <button
                onClick={() => setShowModal(true)}
                className="btn btn-primary"
              >
                Create your first API key
              </button>
            }
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="table-header">Name</th>
                <th className="table-header">Permissions</th>
                <th className="table-header">Access</th>
                {isSuperAdmin && <th className="table-header">Created By</th>}
                <th className="table-header">Last Used</th>
                <th className="table-header">Expires</th>
                <th className="table-header">Created</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {keys.map((key) => (
                <tr key={key.id}>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4 text-slate-400" />
                      <span className="font-medium text-slate-900">{key.name}</span>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex flex-col gap-1">
                      <div className="flex flex-wrap gap-1">
                        {key.permissions.map((perm) => (
                          <span
                            key={perm}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              perm === 'super_admin'
                                ? 'bg-red-100 text-red-700'
                                : perm === 'admin'
                                ? 'bg-purple-100 text-purple-700'
                                : perm === 'classify'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {perm}
                          </span>
                        ))}
                      </div>
                      {key.allowed_domain_types && key.allowed_domain_types.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-0.5" title={`Scoped to: ${key.allowed_domain_types.join(', ')}`}>
                          {key.allowed_domain_types.map((t) => (
                            <span
                              key={t}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex flex-wrap gap-1">
                      {key.show_cost && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                          <DollarSign className="h-3 w-3" />Cost
                        </span>
                      )}
                      {key.show_mind_map && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                          <Network className="h-3 w-3" />Map
                        </span>
                      )}
                      {key.show_acquisition_method && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                          <ShoppingCart className="h-3 w-3" />Acq
                        </span>
                      )}
                      {key.show_whois && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200">
                          <Search className="h-3 w-3" />WHOIS
                        </span>
                      )}
                      {key.show_footprint_audit && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
                          <ShieldAlert className="h-3 w-3" />Footprint
                        </span>
                      )}
                      {key.can_trigger_sync && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <RefreshCw className="h-3 w-3" />Sync
                        </span>
                      )}
                      {key.can_manage_nameservers && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                          <Globe className="h-3 w-3" />NS
                        </span>
                      )}
                      {key.can_renew_domains && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
                          <CalendarClock className="h-3 w-3" />Renew
                        </span>
                      )}
                      {!key.show_cost && !key.show_mind_map && !key.show_acquisition_method && !key.show_whois && !key.show_footprint_audit && !key.can_trigger_sync && !key.can_manage_nameservers && !key.can_renew_domains && (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </div>
                  </td>
                  {isSuperAdmin && (
                    <td className="table-cell text-slate-500 text-sm">
                      {key.created_by_username || <span className="text-slate-400">—</span>}
                    </td>
                  )}
                  <td className="table-cell text-slate-500 text-sm">
                    {key.last_used_at
                      ? format(new Date(key.last_used_at), 'MMM d, yyyy h:mm a')
                      : 'Never'}
                  </td>
                  <td className="table-cell text-sm">
                    {key.expires_at ? (
                      <span className={
                        new Date(key.expires_at) < new Date()
                          ? 'text-red-600'
                          : 'text-slate-500'
                      }>
                        {format(new Date(key.expires_at), 'MMM d, yyyy')}
                      </span>
                    ) : (
                      <span className="text-slate-400">Never</span>
                    )}
                  </td>
                  <td className="table-cell text-slate-500 text-sm">
                    {format(new Date(key.created_at), 'MMM d, yyyy')}
                  </td>
                  <td className="table-cell text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => setEditingKey(key)}
                        className="p-1.5 text-slate-400 hover:text-brand-600"
                        title="Edit scope and visibility"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(key)}
                        className="p-1.5 text-slate-400 hover:text-red-600"
                        title="Delete API key"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 card p-4">
        <h3 className="text-sm font-medium text-slate-900 mb-2">Usage</h3>
        <p className="text-sm text-slate-500 mb-3">
          Include the API key in requests using the Authorization header:
        </p>
        <pre className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 overflow-x-auto">{`curl -H "Authorization: Bearer dm_your_key_here" ${window.location.origin}/api/domains`}</pre>
      </div>

      {showModal && (
        <CreateKeyModal
          onClose={() => setShowModal(false)}
          onSuccess={(key) => {
            setShowModal(false);
            setNewKey(key);
            loadKeys();
          }}
        />
      )}

      {editingKey && (
        <EditKeyModal
          apiKey={editingKey}
          onClose={() => setEditingKey(null)}
          onSuccess={() => {
            setEditingKey(null);
            loadKeys();
          }}
        />
      )}
    </div>
  );
}

function NewKeyBanner({ apiKey, onDismiss }: { apiKey: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-medium text-yellow-800">
            Save your API key now — it won't be shown again
          </h3>
          <div className="mt-2 flex items-center gap-2">
            <code className="bg-surface px-3 py-1.5 rounded border border-yellow-200 text-sm font-mono text-slate-900">
              {apiKey}
            </code>
            <button
              onClick={handleCopy}
              className="p-1.5 text-yellow-700 hover:text-yellow-900"
              title="Copy to clipboard"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <button onClick={onDismiss} className="text-yellow-500 hover:text-yellow-700">
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

function CreateKeyModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (key: string) => void;
}) {
  const { fetchApi, hasPermission, user } = useApi();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  // Default new keys to the caller's own role so the typical case (a user
  // creating a key that behaves like their account) is one click.
  const [permissions, setPermissions] = useState<string[]>(() => {
    if (!user) return ['read'];
    return user.type === 'super_admin' ? ['admin'] : [user.type];
  });
  const [expiresAt, setExpiresAt] = useState('');
  // Default to unrestricted so the common case is one click. Users who
  // need a scoped key untick this and pick types explicitly.
  const [scopeUnrestricted, setScopeUnrestricted] = useState(true);
  const [scopeTypes, setScopeTypes] = useState<string[]>([]);
  const [availableDomainTypes, setAvailableDomainTypes] = useState<string[]>([]);
  const [showCost, setShowCost] = useState(false);
  const [showAcquisition, setShowAcquisition] = useState(false);
  const [showWhois, setShowWhois] = useState(false);
  const [showFootprintAudit, setShowFootprintAudit] = useState(false);
  const [canTriggerSync, setCanTriggerSync] = useState(false);
  const [canManageNameservers, setCanManageNameservers] = useState(false);
  const [canRenewDomains, setCanRenewDomains] = useState(false);

  const callerCanSeeCost = !!user?.show_cost || hasPermission('super_admin');
  const callerCanSeeAcquisition = !!user?.show_acquisition_method || hasPermission('super_admin');
  const callerCanSeeWhois = !!user?.show_whois || hasPermission('admin');
  const callerCanSeeFootprintAudit = !!user?.show_footprint_audit || hasPermission('admin');
  const callerCanTriggerSync = !!user?.can_trigger_sync || hasPermission('admin');
  const callerCanManageNameservers = !!user?.can_manage_nameservers || hasPermission('admin');
  const callerCanRenewDomains = !!user?.can_renew_domains || hasPermission('super_admin');

  // Pull live domain types from the same endpoint that powers the filter
  // dropdowns. Keeps the scope picker in sync as new types are classified.
  useEffect(() => {
    fetchApi<ApiResponse<Record<string, string[]>>>('/reports/filter-options')
      .then(res => {
        if (res.data?.domain_type) setAvailableDomainTypes(res.data.domain_type);
      })
      .catch(() => { /* not critical */ });
  }, [fetchApi]);

  const togglePermission = (perm: string) => {
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const body: Record<string, unknown> = { name, permissions };
      if (expiresAt) body.expires_at = expiresAt;
      // Always send scope explicitly: null = unrestricted, array = restricted.
      // The backend defaults an omitted field to [] (no access), so silence
      // here would mint a key that sees nothing.
      body.allowed_domain_types = scopeUnrestricted ? null : scopeTypes;
      body.show_cost = showCost;
      body.show_acquisition_method = showAcquisition;
      body.show_whois = showWhois;
      body.show_footprint_audit = showFootprintAudit;
      body.can_trigger_sync = canTriggerSync;
      body.can_manage_nameservers = canManageNameservers;
      if (callerCanRenewDomains) body.can_renew_domains = canRenewDomains;

      const res = await fetchApi<ApiResponse<{ key: string }>>('/api-keys', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (res.data?.key) {
        onSuccess(res.data.key);
      }
    } catch (error) {
      toast.error('Failed to create API key', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Only show permissions the caller actually holds — you can't grant what
  // you don't have, and the backend will reject it anyway.
  const allPermissions = [
    { key: 'read', label: 'Read', desc: 'View domains, tags, and reports' },
    { key: 'classify', label: 'Classify', desc: 'Edit classification fields, append to notes, create and apply tags' },
    { key: 'admin', label: 'Admin', desc: 'Full administrative access' },
    { key: 'super_admin', label: 'Super Admin', desc: 'Overview of all API keys across all users' },
  ].filter((p) => hasPermission(p.key));

  return (
    <Modal
      title="Create API Key"
      onClose={onClose}
      size="lg"
      dismissible={!loading}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" form="create-key-form" disabled={loading} className="btn btn-primary">
            {loading ? 'Creating...' : 'Create Key'}
          </button>
        </>
      }
    >
        <form id="create-key-form" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="keyName" className="label">Name *</label>
              <input
                id="keyName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., CI/CD Pipeline, Monitoring Script"
                className="input"
                required
              />
            </div>

            <div>
              <label className="label">Permissions</label>
              <div className="space-y-2">
                {allPermissions.map((perm) => (
                  <label
                    key={perm.key}
                    className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={permissions.includes(perm.key)}
                      onChange={() => togglePermission(perm.key)}
                      className="mt-0.5 h-4 w-4 text-brand-600 rounded border-slate-300"
                    />
                    <div>
                      <div className="text-sm font-medium text-slate-900">{perm.label}</div>
                      <div className="text-xs text-slate-500">{perm.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="expiresAt" className="label">
                Expiration (optional)
              </label>
              <input
                id="expiresAt"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Leave blank for a key that never expires.
              </p>
            </div>

            <div>
              <label className="label">Domain-type scope</label>
              <p className="text-xs text-slate-500 mb-2">
                Restrict this key to only see domains of certain types. Super-admin keys always bypass scope.
              </p>
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={scopeUnrestricted}
                  onChange={(e) => setScopeUnrestricted(e.target.checked)}
                  className="h-4 w-4 text-brand-600 border-slate-300 rounded"
                />
                <span className="text-sm">Unrestricted (sees all domains)</span>
              </label>
              {!scopeUnrestricted && (
                <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                  {availableDomainTypes.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No classified domain types found yet. Classify some domains first to populate this list.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {availableDomainTypes.map((t) => (
                        <label key={t} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={scopeTypes.includes(t)}
                            onChange={(e) => {
                              if (e.target.checked) setScopeTypes([...scopeTypes, t]);
                              else setScopeTypes(scopeTypes.filter(s => s !== t));
                            }}
                            className="h-4 w-4 text-brand-600 border-slate-300 rounded"
                          />
                          <span className="text-sm">{t}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {scopeTypes.length === 0 && availableDomainTypes.length > 0 && (
                    <p className="text-xs text-amber-600 mt-2">
                      No types selected — this key will see no domains until you select at least one or switch to Unrestricted.
                    </p>
                  )}
                </div>
              )}
            </div>

            {(callerCanSeeCost || callerCanSeeAcquisition || callerCanSeeWhois || callerCanSeeFootprintAudit) && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="h-4 w-4 text-slate-400" />
                  <label className="label mb-0">Access &amp; Visibility</label>
                </div>
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                  {callerCanSeeCost && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={showCost}
                        onChange={(e) => setShowCost(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <DollarSign className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Cost & Currency</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can read acquisition cost and currency fields</p>
                      </div>
                    </label>
                  )}
                  {callerCanSeeAcquisition && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={showAcquisition}
                        onChange={(e) => setShowAcquisition(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <ShoppingCart className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Acquisition Method</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can read how each domain was acquired</p>
                      </div>
                    </label>
                  )}
                  {callerCanSeeWhois && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={showWhois}
                        onChange={(e) => setShowWhois(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <Search className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">WHOIS</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can read WHOIS data and search (lookups stay admin-only)</p>
                      </div>
                    </label>
                  )}
                  {callerCanSeeFootprintAudit && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={showFootprintAudit}
                        onChange={(e) => setShowFootprintAudit(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <ShieldAlert className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Footprint Audit</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can read footprint audit results (running stays admin-only)</p>
                      </div>
                    </label>
                  )}
                  {callerCanTriggerSync && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={canTriggerSync}
                        onChange={(e) => setCanTriggerSync(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <RefreshCw className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Trigger Sync</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can refresh registrar accounts (no access to credentials)</p>
                      </div>
                    </label>
                  )}
                  {callerCanManageNameservers && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={canManageNameservers}
                        onChange={(e) => setCanManageNameservers(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <Globe className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Manage Nameservers</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can change nameservers at the registrar (live DNS change), within its domain scope</p>
                      </div>
                    </label>
                  )}
                  {callerCanRenewDomains && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={canRenewDomains}
                        onChange={(e) => setCanRenewDomains(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Renew Domains</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can renew domains within its scope — <strong>charges the registrar account</strong></p>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            )}
          </div>
        </form>
    </Modal>
  );
}

// Edit scope + visibility on an existing key. Name, permissions, and
// expires_at are immutable (the backend doesn't accept them on PUT).
function EditKeyModal({
  apiKey,
  onClose,
  onSuccess,
}: {
  apiKey: ApiKeyInfo;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { fetchApi, hasPermission, user } = useApi();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [scopeUnrestricted, setScopeUnrestricted] = useState<boolean>(apiKey.allowed_domain_types == null);
  const [scopeTypes, setScopeTypes] = useState<string[]>(apiKey.allowed_domain_types || []);
  const [availableDomainTypes, setAvailableDomainTypes] = useState<string[]>([]);
  const [showCost, setShowCost] = useState<boolean>(!!apiKey.show_cost);
  const [showMindMap, setShowMindMap] = useState<boolean>(!!apiKey.show_mind_map);
  const [showAcquisition, setShowAcquisition] = useState<boolean>(!!apiKey.show_acquisition_method);
  const [showWhois, setShowWhois] = useState<boolean>(!!apiKey.show_whois);
  const [showFootprintAudit, setShowFootprintAudit] = useState<boolean>(!!apiKey.show_footprint_audit);
  const [canTriggerSync, setCanTriggerSync] = useState<boolean>(!!apiKey.can_trigger_sync);
  const [canManageNameservers, setCanManageNameservers] = useState<boolean>(!!apiKey.can_manage_nameservers);
  const [canRenewDomains, setCanRenewDomains] = useState<boolean>(!!apiKey.can_renew_domains);

  const callerCanSeeCost = !!user?.show_cost || hasPermission('super_admin');
  const callerCanSeeMindMap = !!user?.show_mind_map || hasPermission('super_admin');
  const callerCanSeeAcquisition = !!user?.show_acquisition_method || hasPermission('super_admin');
  const callerCanSeeWhois = !!user?.show_whois || hasPermission('admin');
  const callerCanSeeFootprintAudit = !!user?.show_footprint_audit || hasPermission('admin');
  const callerCanTriggerSync = !!user?.can_trigger_sync || hasPermission('admin');
  const callerCanManageNameservers = !!user?.can_manage_nameservers || hasPermission('admin');
  const callerCanRenewDomains = !!user?.can_renew_domains || hasPermission('super_admin');

  useEffect(() => {
    fetchApi<ApiResponse<Record<string, string[]>>>('/reports/filter-options')
      .then(res => {
        if (res.data?.domain_type) setAvailableDomainTypes(res.data.domain_type);
      })
      .catch(() => { /* not critical */ });
  }, [fetchApi]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body: Record<string, unknown> = {
        allowed_domain_types: scopeUnrestricted ? null : scopeTypes,
        show_cost: showCost,
        show_mind_map: showMindMap,
      };
      // Only send the gated acquisition flag when the caller is privy, else the
      // API rejects re-granting a flag the caller doesn't hold.
      if (callerCanSeeAcquisition) body.show_acquisition_method = showAcquisition;
      if (callerCanSeeWhois) body.show_whois = showWhois;
      if (callerCanSeeFootprintAudit) body.show_footprint_audit = showFootprintAudit;
      if (callerCanTriggerSync) body.can_trigger_sync = canTriggerSync;
      if (callerCanManageNameservers) body.can_manage_nameservers = canManageNameservers;
      if (callerCanRenewDomains) body.can_renew_domains = canRenewDomains;
      await fetchApi(`/api-keys/${apiKey.id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      onSuccess();
    } catch (error) {
      toast.error('Failed to update API key', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit API Key"
      onClose={onClose}
      size="lg"
      dismissible={!loading}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" form="edit-key-form" disabled={loading} className="btn btn-primary">
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </>
      }
    >
        <form id="edit-key-form" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="rounded-md bg-slate-50 p-3 text-sm">
              <div className="flex items-center gap-2 text-slate-900">
                <Key className="h-4 w-4 text-slate-400" />
                <span className="font-medium">{apiKey.name}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {apiKey.permissions.map((perm) => (
                  <span
                    key={perm}
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      perm === 'super_admin'
                        ? 'bg-red-100 text-red-700'
                        : perm === 'admin'
                        ? 'bg-purple-100 text-purple-700'
                        : perm === 'classify'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {perm}
                  </span>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Name and permissions are fixed at creation — delete and recreate the key to change them.
              </p>
            </div>

            <div>
              <label className="label">Domain-type scope</label>
              <p className="text-xs text-slate-500 mb-2">
                Restrict this key to only see domains of certain types. Super-admin keys always bypass scope.
              </p>
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={scopeUnrestricted}
                  onChange={(e) => setScopeUnrestricted(e.target.checked)}
                  className="h-4 w-4 text-brand-600 border-slate-300 rounded"
                />
                <span className="text-sm">Unrestricted (sees all domains)</span>
              </label>
              {!scopeUnrestricted && (
                <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                  {availableDomainTypes.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No classified domain types found yet.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {availableDomainTypes.map((t) => (
                        <label key={t} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={scopeTypes.includes(t)}
                            onChange={(e) => {
                              if (e.target.checked) setScopeTypes([...scopeTypes, t]);
                              else setScopeTypes(scopeTypes.filter(s => s !== t));
                            }}
                            className="h-4 w-4 text-brand-600 border-slate-300 rounded"
                          />
                          <span className="text-sm">{t}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {scopeTypes.length === 0 && availableDomainTypes.length > 0 && (
                    <p className="text-xs text-amber-600 mt-2">
                      No types selected — this key will see no domains until you select at least one or switch to Unrestricted.
                    </p>
                  )}
                </div>
              )}
            </div>

            {(callerCanSeeCost || callerCanSeeMindMap || callerCanSeeAcquisition || callerCanSeeWhois || callerCanSeeFootprintAudit) && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Eye className="h-4 w-4 text-slate-400" />
                  <label className="label mb-0">Access &amp; Visibility</label>
                </div>
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                  {callerCanSeeCost && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={showCost}
                        onChange={(e) => setShowCost(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <DollarSign className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Cost & Currency</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can read acquisition cost and currency fields</p>
                      </div>
                    </label>
                  )}
                  {callerCanSeeMindMap && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={showMindMap}
                        onChange={(e) => setShowMindMap(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <Network className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Mind Map</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can access the WHOIS contact mind map (sensitive)</p>
                      </div>
                    </label>
                  )}
                  {callerCanSeeAcquisition && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={showAcquisition}
                        onChange={(e) => setShowAcquisition(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <ShoppingCart className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Acquisition Method</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can read how each domain was acquired</p>
                      </div>
                    </label>
                  )}
                  {callerCanSeeWhois && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={showWhois}
                        onChange={(e) => setShowWhois(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <Search className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">WHOIS</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can read WHOIS data and search (lookups stay admin-only)</p>
                      </div>
                    </label>
                  )}
                  {callerCanSeeFootprintAudit && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={showFootprintAudit}
                        onChange={(e) => setShowFootprintAudit(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <ShieldAlert className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Footprint Audit</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can read footprint audit results (running stays admin-only)</p>
                      </div>
                    </label>
                  )}
                  {callerCanTriggerSync && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={canTriggerSync}
                        onChange={(e) => setCanTriggerSync(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <RefreshCw className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Trigger Sync</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can refresh registrar accounts (no access to credentials)</p>
                      </div>
                    </label>
                  )}
                  {callerCanManageNameservers && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={canManageNameservers}
                        onChange={(e) => setCanManageNameservers(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <Globe className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Manage Nameservers</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can change nameservers at the registrar (live DNS change), within its domain scope</p>
                      </div>
                    </label>
                  )}
                  {callerCanRenewDomains && (
                    <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={canRenewDomains}
                        onChange={(e) => setCanRenewDomains(e.target.checked)}
                        className="mt-0.5 h-4 w-4 text-brand-600 border-slate-300 rounded shrink-0"
                      />
                      <div>
                        <div className="flex items-center gap-1.5">
                          <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-800">Renew Domains</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Key can renew domains within its scope — <strong>charges the registrar account</strong></p>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            )}
          </div>
        </form>
    </Modal>
  );
}
